import { AuthContext, ConfigContext } from "@@components";
import { useCallback, useContext } from "react";
import { ApiException } from "../../api/api";
import { createScopedLogger } from "../logger";
import { notifyDesignerAuthenticationRequired } from "../../UserInterfaceModule/Utils/designerAuthentication";
import { IsDesignMode } from "../../UserInterfaceModule/Utils/runtimeDetection";

const HTTP_UNAUTHORIZED = 401;
const logger = createScopedLogger("useApiErrorHandler");

/**
 * Custom hook to handle API errors, particularly 401 Unauthorized responses
 */
export function useApiErrorHandler() {
  const auth = useContext(AuthContext);
  const { DESIGNER_ORIGIN } = useContext(ConfigContext);

  const handleApiError = useCallback(
    async (error: any) => {
      const shouldRedirectToSignIn = auth.isAuthenticated && auth.authService;

      if (error?.status === HTTP_UNAUTHORIZED && IsDesignMode) {
        notifyDesignerAuthenticationRequired(DESIGNER_ORIGIN, "unauthorized");
        return true;
      }

      // Check if it's an ApiException with 401 status
      if (error instanceof ApiException && error.status === HTTP_UNAUTHORIZED) {
        if (!shouldRedirectToSignIn) {
          logger.warn("API call failed with 401 Unauthorized. Sign-in redirect skipped because no authenticated session is active.");
          return false;
        }

        logger.warn(
          "API call failed with 401 Unauthorized. Redirecting to sign in."
        );

        try {
          const currentPath = window.location.pathname + window.location.search;
          await auth.authService.signIn(currentPath);
        } catch (signInError) {
          logger.error("Error during sign-in redirect:", signInError);
        }

        return true; // Indicates we handled the error
      }

      // Check if it's a Response object with 401 status
      if (error?.status === HTTP_UNAUTHORIZED) {
        if (!shouldRedirectToSignIn) {
          logger.warn("API call failed with 401 Unauthorized. Sign-in redirect skipped because no authenticated session is active.");
          return false;
        }

        logger.warn(
          "API call failed with 401 Unauthorized. Redirecting to sign in."
        );

        try {
          const currentPath = window.location.pathname + window.location.search;
          await auth.authService.signIn(currentPath);
        } catch (signInError) {
          logger.error("Error during sign-in redirect:", signInError);
        }

        return true; // Indicates we handled the error
      }

      return false; // Did not handle the error
    },
    [auth, DESIGNER_ORIGIN]
  );
  return handleApiError;
}

export default useApiErrorHandler;
